import React, { useState } from 'react';
import {
  LockClosedIcon,
  EnvelopeIcon,
  Squares2X2Icon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { LoginRequest } from '@packages/global';
import { appConfig } from '../config/app.config';
import ThemeToggle from '../components/ThemeToggle';
import LanguageSwitcher from '../components/LanguageSwitcher';

const LoginPage: React.FC = () => {
  const { t } = useTranslation('auth');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState<LoginRequest>({ email: '', password: '' });
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const { login } = useAuthStore();
  const navigate = useNavigate();

  const validateForm = (): boolean => {
    const newErrors: { email?: string; password?: string } = {};
    if (!formData.email) {
      newErrors.email = t('login.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('login.emailInvalid');
    }
    if (!formData.password) {
      newErrors.password = t('login.passwordRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      await login(formData);
      navigate('/dashboard');
    } catch (error: any) {
      setErrors({ email: error.response?.data?.error || t('login.failed') });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof LoginRequest) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
    if (errors[field]) setErrors({ ...errors, [field]: undefined });
  };

  return (
    <div className="h-screen bg-bg-page p-4 lg:p-8 overflow-hidden relative">
      {/* 右上角：主题 & 语言切换 */}
      <div className="absolute top-4 right-4 z-20 flex items-center gap-1">
        <ThemeToggle compact />
        <LanguageSwitcher compact />
      </div>
      <div className="relative h-full flex items-center justify-center z-10">
        <div className="w-full max-w-4xl mx-auto flex bg-bg-surface backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-border-subtle">
          {/* 左侧品牌区域 */}
          <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-800 p-8 items-center justify-center relative">
            <div className="absolute inset-0 opacity-30">
              <div className="w-full h-full bg-white/5 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[length:60px_60px]"></div>
            </div>
            <div className="relative text-center text-white max-w-md w-full">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl mb-5 shadow-xl">
                <Squares2X2Icon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-white to-indigo-100 bg-clip-text text-transparent">
                {appConfig.name}
              </h1>
              <p className="text-base text-indigo-100 mb-8 font-light">
                {appConfig.description}
              </p>

              <div className="grid gap-4 w-full">
                <div className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Squares2X2Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-white text-sm">功能管理</h3>
                    <p className="text-xs text-indigo-200">强大的功能模块管理</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Squares2X2Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-white text-sm">用户管理</h3>
                    <p className="text-xs text-indigo-200">完善的用户权限体系</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Squares2X2Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-white text-sm">数据分析</h3>
                    <p className="text-xs text-indigo-200">实时数据统计与分析</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 右侧登录表单 */}
          <div className="w-full lg:w-1/2 p-8 lg:p-16 flex items-center justify-center bg-bg-surface">
            <div className="w-full max-w-sm">
              {/* 移动端 Logo */}
              <div className="lg:hidden text-center mb-8">
                <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${appConfig.icon.gradient} rounded-2xl mb-4`}>
                  <Squares2X2Icon className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-gray-50 mb-1">{appConfig.name}</h1>
                <p className="text-gray-400 text-sm">{appConfig.description}</p>
              </div>

              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-50 mb-2">{t('login.title')}</h2>
                <p className="text-gray-400">{t('login.subtitle')}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-200 mb-2">
                    {t('login.email')}
                  </label>
                  <div className="relative w-full">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="username"
                      value={formData.email}
                      onChange={handleChange('email')}
                      className={`box-border w-full pl-10 pr-3 py-3 border ${errors.email ? 'border-red-400' : 'border-border-default'} rounded-xl bg-bg-form text-gray-50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors [&:-webkit-autofill]:rounded-xl [&:-webkit-autofill:hover]:rounded-xl [&:-webkit-autofill:focus]:rounded-xl`}
                      placeholder={t('login.emailPlaceholder')}
                    />
                  </div>
                  {errors.email && <p className="mt-2 text-sm text-red-500">{errors.email}</p>}
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-200 mb-2">
                    {t('login.password')}
                  </label>
                  <div className="relative w-full">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <LockClosedIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={formData.password}
                      onChange={handleChange('password')}
                      className={`box-border w-full pl-10 pr-10 py-3 border ${errors.password ? 'border-red-400' : 'border-border-default'} rounded-xl bg-bg-form text-gray-50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors [&:-webkit-autofill]:rounded-xl [&:-webkit-autofill:hover]:rounded-xl [&:-webkit-autofill:focus]:rounded-xl`}
                      placeholder={t('login.passwordPlaceholder')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300 transition-colors"
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.password && <p className="mt-2 text-sm text-red-500">{errors.password}</p>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-3 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 border-none font-semibold text-lg text-white transition-all duration-300 cursor-pointer ${loading ? 'opacity-70 cursor-not-allowed' : 'hover:from-indigo-600 hover:to-purple-700 hover:shadow-xl hover:shadow-indigo-500/25 active:scale-95'}`}
                >
                  {loading ? t('login.loggingIn') : t('login.submit')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
